import React, {useCallback} from 'react';

import {PermissionsTable} from 'src/admin-rights-service/components/permissions-table';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';

import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {PermissionsTabSkeleton} from './permissions-tab-skeleton';

import type {AccessRightsProfile} from 'src/openapi/admin/models/AccessRightsProfile';


type Props = {
    profile: AccessRightsProfile,
    isLoading: boolean,
    profileHistory?: React.ReactNode,
    onSetDescription: (description: string) => void,
    onSetPermissions: (permissions: string[]) => void,
    disabled?: boolean,
};

export const PermissionsTab = (props: Props) => {
    const {profile, onSetPermissions, onSetDescription, isLoading, profileHistory, disabled} = props;
    const {hasAccess} = useAdminAccessRight();

    const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onSetDescription(event.target.value);
    }, [onSetDescription]);

    const handleDescriptionBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        onSetDescription(event.currentTarget.value.trim());
    }, [onSetDescription]);

    const editDisabled = disabled || !hasAccess(ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE);

    return (
        <Skeleton shown={isLoading} view={<PermissionsTabSkeleton/>}>
            <Item className="group-item pd-l">
                <Group alignItems="stretch" gap="xxxl">
                    <WidthItem width={840}>
                        <FormSection headline="Role Permissions">
                            <ValidatedControl name="profile-right-codes">
                                {() => <span/>}
                            </ValidatedControl>
                            <WidthItem width={840}>
                                <PermissionsTable
                                    permissions={profile.rightsCodes}
                                    setPermissions={onSetPermissions}
                                    disabled={editDisabled}
                                />
                            </WidthItem>
                        </FormSection>
                    </WidthItem>
                    <Item grow={1}>
                        <FormSection headline="Role Description">
                            {editDisabled ?
                                profile.profileDescription : (
                                    <ValidatedControl name="profile-description">
                                        {invalid => (
                                            <WidthItem width="100%">
                                                <Textarea
                                                    maxLength={255}
                                                    resizable={false}
                                                    placeholder="Role description"
                                                    disabled={editDisabled}
                                                    value={profile.profileDescription}
                                                    color={invalid ? 'ERROR' : 'DEFAULT'}
                                                    onChange={handleDescriptionChange}
                                                    onBlur={handleDescriptionBlur}
                                                />
                                            </WidthItem>
                                        )}
                                    </ValidatedControl>
                                )}
                        </FormSection>
                        {profileHistory}
                    </Item>
                </Group>
            </Item>
        </Skeleton>
    );
};
